import type { SearchPort } from '../../domain/search/SearchPort.js'
import type { SearchResponse } from '../../domain/search/SearchModels.js'
import type { AlgoliaResponseDTO } from './AlgoliaResponseDTO.js'
import { normalizeAlgoliaResponse } from './normalizeAlgoliaResponse.js'

export interface AlgoliaAdapterConfig {
  proxyUrl: string
  env: string
  limit?: number
}

/**
 * Algolia implementation of SearchPort.
 *
 * This class owns ALL Algolia and HTTP knowledge:
 *   - POST request shape
 *   - env/limit params
 *   - response parsing
 *   - in-flight request cancellation via AbortController
 *
 * The rest of the application never sees any of this.
 */
export class AlgoliaSearchAdapter implements SearchPort {
  private abortController: AbortController | null = null

  constructor(private config: AlgoliaAdapterConfig) {}

  async search(query: string): Promise<SearchResponse> {
    // Cancel any previous in-flight request before issuing a new one
    this.abortController?.abort()
    this.abortController = new AbortController()

    const response = await fetch(this.config.proxyUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query,
        env: this.config.env,
        limit: this.config.limit ?? 5,
      }),
      signal: this.abortController.signal,
    })

    if (!response.ok) {
      throw new Error(`Search failed: ${response.status} ${response.statusText}`)
    }

    const dto: AlgoliaResponseDTO = await response.json()
    return normalizeAlgoliaResponse(dto)
  }

  destroy(): void {
    this.abortController?.abort()
    this.abortController = null
  }
}
